import type {ShallowRef} from 'vue';
import {onMounted, onUnmounted, shallowRef} from 'vue';
import {useIntersectionObserver} from '@malt/utils';

export type HeroSequence = {
    isVideoFadingOut: ShallowRef<boolean>;
    isUiSequencePlaying: ShallowRef<boolean>;
    isUiSequencePaused: ShallowRef<boolean>;
};

export const HERO_VIDEO_FADE_OUT_DURATION_IN_SECONDS = 0.5;

// The camera of the video settles at this timestamp, the UI composition is choreographed from there.
export const HERO_UI_SEQUENCE_START_IN_SECONDS = 1.75;

export function useHeroSequence(video: Readonly<ShallowRef<HTMLVideoElement | null>>): HeroSequence {
    const isVideoFadingOut = shallowRef(false);
    const isUiSequencePlaying = shallowRef(false);
    const isUiSequencePaused = shallowRef(false);

    const frameId = shallowRef<number | null>(null);
    const clockRunning = shallowRef(false);
    const disposed = shallowRef(false);
    const prefersReducedMotion = shallowRef(false);

    function onIntersection(entries: IntersectionObserverEntry[]) {
        if (prefersReducedMotion.value || disposed.value) {
            return;
        }

        if (entries[0]?.isIntersecting) {
            void playInViewport();
            return;
        }

        pauseOutOfViewport();
    }

    const {isIntersecting, startObserving} = useIntersectionObserver(video, onIntersection, {
        immediate: false,
        options: {threshold: 0},
    });

    // `timeupdate` only fires about four times per second, which is too coarse to start the composition on an
    // exact timestamp, so the video clock is polled on every frame instead.
    function syncOnVideoTime() {
        frameId.value = null;

        if (!clockRunning.value) {
            return;
        }

        const element = video.value;

        if (element && Number.isFinite(element.duration)) {
            // The loop restarts currentTime at 0, which turns the fade-out back into a fade-in and rewinds the
            // UI sequence to its initial state until the next start mark.
            isVideoFadingOut.value = element.duration - element.currentTime <= HERO_VIDEO_FADE_OUT_DURATION_IN_SECONDS;
            isUiSequencePlaying.value = element.currentTime >= HERO_UI_SEQUENCE_START_IN_SECONDS;
        }

        frameId.value = window.requestAnimationFrame(syncOnVideoTime);
    }

    function startClock() {
        if (clockRunning.value) {
            return;
        }

        clockRunning.value = true;
        frameId.value = window.requestAnimationFrame(syncOnVideoTime);
    }

    function stopClock() {
        clockRunning.value = false;

        if (frameId.value !== null) {
            window.cancelAnimationFrame(frameId.value);
            frameId.value = null;
        }
    }

    function pauseOutOfViewport() {
        pauseVideo(video.value);
        stopClock();
        isUiSequencePaused.value = true;
    }

    async function playInViewport() {
        const isVideoPlaying = await startVideo(video.value);

        if (disposed.value || !isIntersecting.value) {
            pauseOutOfViewport();
            return;
        }

        isUiSequencePaused.value = false;

        if (!isVideoPlaying) {
            // Without a video clock to follow, the composition plays once on its own.
            isUiSequencePlaying.value = true;
            return;
        }

        startClock();
    }

    onMounted(() => {
        prefersReducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (prefersReducedMotion.value) {
            pauseVideo(video.value);
            return;
        }

        if (!video.value) {
            isUiSequencePlaying.value = true;
            return;
        }

        startObserving();
    });

    onUnmounted(() => {
        disposed.value = true;
        stopClock();
    });

    return {isVideoFadingOut, isUiSequencePlaying, isUiSequencePaused};
}

function pauseVideo(video: HTMLVideoElement | null) {
    video?.pause();
}

async function startVideo(video: HTMLVideoElement | null): Promise<boolean> {
    if (!video) {
        return false;
    }

    // Autoplay is only reliable when these are set as properties, the template attributes alone are not enough.
    video.muted = true;
    video.loop = true;
    video.playsInline = true;

    try {
        await video.play();
        return true;
    } catch {
        // Autoplay can be refused by the browser, the first frame stays displayed.
        return false;
    }
}
